import {
  SeatSectionStatus,
  type SeatRowConfig,
  type SeatSectionConfig,
} from "client";

/**
 * What you may change about a seats section, and when.
 *
 * Seat identity is (seatSectionId, row, seat). Once ACTIVE, anything that would
 * make an existing (row, seat) mean a different physical seat, stop existing,
 * or move in space (spacing / seat size) is forbidden. Label naming fields stay
 * editable. Shared by the write path and the builder form.
 */

export type SeatSectionChangeIssue = {
  field: string;
  message: string;
};

export type SeatSectionRuleInput = {
  current: SeatSectionConfig | null | undefined;
  next: Partial<SeatSectionConfig> | null | undefined;
  /** ShapeType string when the shape type is also being changed. */
  nextShapeType?: string | null;
  currentShapeType?: string | null;
  /** True when the polygon vertices / curves are part of the same edit. */
  polygonGeometryChanged?: boolean;
  /** True when width/height/radius (or equivalent body size) changed. */
  sizeChanged?: boolean;
};

/** Fields that only change painted labels, not (row, seat) or seat positions. */
export const ALWAYS_EDITABLE_SEAT_FIELDS = [
  "rowNamingScheme",
  "firstRowName",
  "seatNumberingDirection",
  "firstSeatNumber",
] as const;

/** Layout fields frozen once ACTIVE — changing them moves seats in space. */
const LOCKED_LAYOUT_FIELDS = [
  "rowSpacing",
  "seatSpacing",
  "seatWidth",
  "seatHeight",
  "rowCurve",
] as const;

export const isSeatSectionLocked = (
  config: SeatSectionConfig | null | undefined
): boolean => config?.status === SeatSectionStatus.ACTIVE;

/** Shown when builder/API tries to delete an ACTIVE seats section. */
export const ACTIVE_SEAT_SECTION_DELETE_BLOCKED =
  "Cannot delete an active seats section — seats may already be held or sold";

const rowByIndex = (
  rowsConfig: readonly SeatRowConfig[] | null | undefined
): Map<number, SeatRowConfig> => {
  const map = new Map<number, SeatRowConfig>();
  for (const row of rowsConfig ?? []) map.set(row.rowIndex, row);
  return map;
};

/** Returns the reasons this edit must be rejected. Empty array == allowed. */
export const validateSeatSectionChange = ({
  current,
  next,
  nextShapeType,
  currentShapeType,
  polygonGeometryChanged,
  sizeChanged,
}: SeatSectionRuleInput): SeatSectionChangeIssue[] => {
  if (!next) return [];

  // A DRAFT section has never been held or sold — everything is fair game.
  if (!isSeatSectionLocked(current) || !current) return [];

  const issues: SeatSectionChangeIssue[] = [];

  for (const field of LOCKED_LAYOUT_FIELDS) {
    const nextValue = next[field];
    if (nextValue === undefined) continue;
    // Older ACTIVE sections may omit rowCurve; treat missing as 0 (straight).
    const currentValue =
      field === "rowCurve" ? (current[field] ?? 0) : current[field];
    const normalizedNext =
      field === "rowCurve" ? (nextValue ?? 0) : nextValue;
    if (normalizedNext !== currentValue) {
      issues.push({
        field,
        message: `Cannot change ${field} on a section that already has sold or held seats — seat positions are locked.`,
      });
    }
  }

  if (next.rows !== undefined && next.rows < current.rows) {
    issues.push({
      field: "rows",
      message: `Cannot reduce rows from ${current.rows} to ${next.rows}: seats in the removed rows may already be sold. Rows can only be added.`,
    });
  }

  if (next.rowsConfig !== undefined) {
    const currentRows = rowByIndex(current.rowsConfig);
    const nextRows = rowByIndex(next.rowsConfig);

    for (const [rowIndex, currentRow] of currentRows) {
      const nextRow = nextRows.get(rowIndex);
      if (!nextRow) {
        issues.push({
          field: `rowsConfig.${rowIndex}`,
          message: `Row ${rowIndex} cannot be removed or reordered — its seat identities are already in use.`,
        });
        continue;
      }
      if (nextRow.seatCount < currentRow.seatCount) {
        issues.push({
          field: `rowsConfig.${rowIndex}.seatCount`,
          message: `Row ${rowIndex} cannot shrink from ${currentRow.seatCount} to ${nextRow.seatCount} seats. Seats can only be appended.`,
        });
      }
      if ((nextRow.startCol ?? 0) !== (currentRow.startCol ?? 0)) {
        issues.push({
          field: `rowsConfig.${rowIndex}.startCol`,
          message: `Row ${rowIndex} cannot shift sideways: that would move every seat in the row to a different position than the one it was sold as.`,
        });
      }
      const currentRemoved = new Set(currentRow.removedSeats ?? []);
      const nextRemoved = new Set(nextRow.removedSeats ?? []);
      for (const seat of nextRemoved) {
        if (!currentRemoved.has(seat) && seat < currentRow.seatCount) {
          issues.push({
            field: `rowsConfig.${rowIndex}.removedSeats`,
            message: `Row ${rowIndex} cannot delete seat ${seat} — seat identities are already in use.`,
          });
          break;
        }
      }
      for (const seat of currentRemoved) {
        if (!nextRemoved.has(seat)) {
          issues.push({
            field: `rowsConfig.${rowIndex}.removedSeats`,
            message: `Row ${rowIndex} cannot restore removed seat ${seat} — seat identities are locked.`,
          });
          break;
        }
      }
      const currentUserRemoved = new Set(currentRow.userRemovedSeats ?? []);
      const nextUserRemoved = new Set(nextRow.userRemovedSeats ?? []);
      for (const seat of nextUserRemoved) {
        if (!currentUserRemoved.has(seat) && seat < currentRow.seatCount) {
          issues.push({
            field: `rowsConfig.${rowIndex}.userRemovedSeats`,
            message: `Row ${rowIndex} cannot delete seat ${seat} — seat identities are already in use.`,
          });
          break;
        }
      }
      for (const seat of currentUserRemoved) {
        if (!nextUserRemoved.has(seat)) {
          issues.push({
            field: `rowsConfig.${rowIndex}.userRemovedSeats`,
            message: `Row ${rowIndex} cannot restore removed seat ${seat} — seat identities are locked.`,
          });
          break;
        }
      }
    }
  }

  if (
    nextShapeType &&
    currentShapeType &&
    nextShapeType !== currentShapeType
  ) {
    issues.push({
      field: "shapeType",
      message:
        "Cannot change the shape type of a section that already has sold or held seats.",
    });
  }

  if (sizeChanged) {
    issues.push({
      field: "size",
      message:
        "Cannot resize a section that already has sold or held seats — seat identities are locked.",
    });
  }

  if (polygonGeometryChanged) {
    issues.push({
      field: "position",
      message:
        "Cannot edit the outline of a section that already has sold or held seats — it would silently delete the seats that fall outside the new outline.",
    });
  }

  return issues;
};

/**
 * A section becomes ACTIVE at the first non-DRAFT override OR when its tier
 * opens for sale — whichever comes first.
 */
export const shouldActivateSeatSection = (input: {
  config: SeatSectionConfig | null | undefined;
  hasNonDraftOverride: boolean;
  isTierSaleOpen: boolean;
}): boolean =>
  !!input.config &&
  input.config.status === SeatSectionStatus.DRAFT &&
  (input.hasNonDraftOverride || input.isTierSaleOpen);

/** Bounds check for a (row, seat) pair against the persisted grid. */
export const isSeatWithinConfig = (
  config: SeatSectionConfig | null | undefined,
  row: number,
  seat: number
): boolean => {
  if (!config) return false;
  if (!Number.isInteger(row) || !Number.isInteger(seat)) return false;
  if (row < 0 || seat < 0) return false;

  const rowsConfig = config.rowsConfig;
  if (rowsConfig && rowsConfig.length > 0) {
    const rowConfig = rowsConfig.find((entry) => entry.rowIndex === row);
    if (!rowConfig || seat >= rowConfig.seatCount) return false;
    return !(rowConfig.removedSeats ?? []).includes(seat);
  }
  return row < config.rows && seat < config.seatsPerRow;
};
